import React, { useEffect, useState, useRef, useCallback } from "react";

/**
 * C1 Grace Margin Diagnostic Panel (v2.14.2).
 *
 * Live view of the Global Shazam Pacer counters, with BEFORE/AFTER
 * delta tracking from the moment the page was opened. Auto-refreshes
 * every 10 seconds. Read-only.
 *
 * The main answer this page must give you:
 *   "Did enabling C1 convert deferred recognitions into successful
 *    Apple recognitions, without introducing failures?"
 *
 * Watch the three C1 outcome counters:
 *   grace_hits_total       ← real recognitions saved by C1
 *   grace_nomatch_total    ← Apple was reached but returned no match
 *   grace_failures_total   ← C1-admitted calls that failed downstream
 *
 * Public page, no auth required (matches the Group A diagnostic
 * endpoints).
 */

const REFRESH_INTERVAL_MS = 10_000;
const API = process.env.REACT_APP_BACKEND_URL;

const formatNumber = (n) =>
  typeof n === "number" ? n.toLocaleString() : "—";

const formatDelta = (n) => {
  if (typeof n !== "number") return "—";
  if (n > 0) return `+${n.toLocaleString()}`;
  if (n < 0) return n.toLocaleString();
  return "0";
};

const StatCard = ({
  label,
  value,
  delta,
  hint,
  emphasise = false,
  testId,
}) => {
  const isPositiveDelta = typeof delta === "number" && delta > 0;
  return (
    <div
      data-testid={testId}
      className={`rounded-xl p-4 border ${
        emphasise
          ? "bg-emerald-950/40 border-emerald-800"
          : "bg-slate-900/60 border-slate-800"
      }`}
    >
      <div className="text-xs uppercase tracking-wide text-slate-400">
        {label}
      </div>
      <div className="mt-1 text-3xl font-mono font-semibold text-slate-100">
        {formatNumber(value)}
      </div>
      {typeof delta === "number" && (
        <div
          className={`mt-1 text-sm font-mono ${
            isPositiveDelta ? "text-emerald-400" : "text-slate-500"
          }`}
        >
          Δ {formatDelta(delta)} since page load
        </div>
      )}
      {hint && (
        <div className="mt-2 text-[11px] text-slate-500 leading-snug">
          {hint}
        </div>
      )}
    </div>
  );
};

const FlagPill = ({ enabled, label, testId }) => (
  <span
    data-testid={testId}
    className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${
      enabled
        ? "bg-emerald-950 text-emerald-300 border border-emerald-800"
        : "bg-slate-900 text-slate-400 border border-slate-800"
    }`}
  >
    <span
      className={`w-2 h-2 rounded-full ${
        enabled ? "bg-emerald-400" : "bg-slate-500"
      }`}
    />
    {label}: {enabled ? "ENABLED" : "DISABLED"}
  </span>
);

const C1Diagnostics = () => {
  const [snapshot, setSnapshot] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const baselineRef = useRef(null);

  const fetchSnapshot = useCallback(async () => {
    try {
      const res = await fetch(
        `${API}/api/admin/diagnostic/group-a/pacer-live`
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSnapshot(data);
      setLastRefresh(new Date());
      setError(null);
      if (!baselineRef.current) {
        baselineRef.current = data;
        setBaseline(data);
      }
    } catch (e) {
      setError(e.message || String(e));
    }
  }, []);

  useEffect(() => {
    fetchSnapshot();
    const id = setInterval(fetchSnapshot, REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchSnapshot]);

  const resetBaseline = () => {
    baselineRef.current = snapshot;
    setBaseline(snapshot);
  };

  const pacer = snapshot?.pacer;
  const basePacer = baseline?.pacer;
  const config = pacer?.config;
  const counters = pacer?.counters;
  const baseCounters = basePacer?.counters;

  const delta = (key) =>
    counters && baseCounters
      ? (counters[key] ?? 0) - (baseCounters[key] ?? 0)
      : undefined;

  // Derived KPIs
  const c1Admits = counters?.grace_margin_admits_total ?? 0;
  const c1Hits = counters?.grace_hits_total ?? 0;
  const c1Nomatch = counters?.grace_nomatch_total ?? 0;
  const c1Failures = counters?.grace_failures_total ?? 0;
  const c1AccountedFor = c1Hits + c1Nomatch + c1Failures;
  const c1HitRate =
    c1AccountedFor > 0
      ? ((c1Hits / c1AccountedFor) * 100).toFixed(1)
      : "—";
  const c1FailureRate =
    c1AccountedFor > 0
      ? ((c1Failures / c1AccountedFor) * 100).toFixed(1)
      : "—";

  return (
    <div
      className="min-h-screen bg-slate-950 text-slate-100 p-6"
      data-testid="c1-diagnostics-page"
    >
      <div className="max-w-6xl mx-auto">
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              C1 Grace Margin — Live Diagnostics
            </h1>
            <p className="mt-2 text-slate-400 text-sm">
              Real-time pacer counters. Auto-refresh every 10 s. Δ =
              change since this page was opened.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <button
              onClick={resetBaseline}
              className="px-3 py-1.5 text-xs rounded-md bg-slate-800 hover:bg-slate-700 border border-slate-700"
              data-testid="reset-baseline-btn"
            >
              Reset Δ baseline
            </button>
            <div className="text-xs text-slate-500">
              Last refresh:{" "}
              {lastRefresh ? lastRefresh.toLocaleTimeString() : "—"}
            </div>
          </div>
        </div>

        {error && (
          <div
            className="mb-6 p-3 rounded-md bg-red-950/60 border border-red-800 text-red-300 text-sm"
            data-testid="c1-diag-error"
          >
            Error fetching diagnostics: {error}
          </div>
        )}

        {/* Config strip */}
        {config && (
          <div
            className="flex flex-wrap gap-3 mb-8"
            data-testid="c1-config-strip"
          >
            <FlagPill
              enabled={config.grace_margin_enabled}
              label="C1 Grace Margin"
              testId="c1-grace-flag"
            />
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono bg-slate-900 text-slate-300 border border-slate-800">
              EXTRA_MARGIN: {config.extra_margin_sec ?? 0} s
            </span>
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono bg-slate-900 text-slate-300 border border-slate-800">
              MIN_INTERVAL: {config.min_interval_sec} s
            </span>
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono bg-slate-900 text-slate-300 border border-slate-800">
              MAX_WAIT: {config.max_wait_sec} s
            </span>
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono bg-slate-900 text-slate-300 border border-slate-800">
              boot:{" "}
              {snapshot?.process_boot_ts
                ? new Date(snapshot.process_boot_ts).toLocaleString()
                : "—"}
            </span>
          </div>
        )}

        {/* Pacer core counters */}
        <h2 className="text-lg font-semibold mb-3 text-slate-200">
          Pacer core
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <StatCard
            testId="stat-deferred"
            label="Deferred total"
            value={counters?.deferred_total}
            delta={delta("deferred_total")}
            hint="Recognitions rejected before Apple call. Under C1 this should slow down."
          />
          <StatCard
            testId="stat-delayed"
            label="Delayed total"
            value={counters?.delayed_total}
            delta={delta("delayed_total")}
            hint="Recognitions that waited (≤ MAX_WAIT) then executed successfully."
          />
          <StatCard
            testId="stat-wait-accum"
            label="Wait accumulated (s)"
            value={counters?.wait_accum_s}
            delta={delta("wait_accum_s")}
            hint="Total seconds slept by delayed callers. avg = wait_accum / delayed."
          />
        </div>

        {/* C1 attribution */}
        <h2 className="text-lg font-semibold mb-3 text-slate-200">
          C1 Grace Margin outcome attribution (v2.14.2)
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <StatCard
            testId="stat-grace-admits"
            label="Grace admits total"
            value={c1Admits}
            delta={delta("grace_margin_admits_total")}
            hint="Callers who would otherwise defer, promoted to delayed."
          />
          <StatCard
            testId="stat-grace-hits"
            label="Grace HITS"
            value={c1Hits}
            delta={delta("grace_hits_total")}
            hint="Real Shazam recognitions saved by C1."
            emphasise
          />
          <StatCard
            testId="stat-grace-nomatch"
            label="Grace NOMATCH"
            value={c1Nomatch}
            delta={delta("grace_nomatch_total")}
            hint="Apple returned no match (expected for silence / talk / jingle)."
          />
          <StatCard
            testId="stat-grace-failures"
            label="Grace FAILURES"
            value={c1Failures}
            delta={delta("grace_failures_total")}
            hint="Timeouts / exceptions / swallowed 4xx-5xx. Should stay near 0."
          />
        </div>

        {/* Derived KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
          <div
            className="rounded-xl p-4 border bg-slate-900/60 border-slate-800"
            data-testid="kpi-c1-hit-rate"
          >
            <div className="text-xs uppercase tracking-wide text-slate-400">
              C1 HIT rate on attempted
            </div>
            <div className="mt-1 text-3xl font-mono font-semibold text-emerald-300">
              {c1HitRate}%
            </div>
            <div className="mt-2 text-[11px] text-slate-500 leading-snug">
              grace_hits / (grace_hits + grace_nomatch + grace_failures)
            </div>
          </div>
          <div
            className="rounded-xl p-4 border bg-slate-900/60 border-slate-800"
            data-testid="kpi-c1-failure-rate"
          >
            <div className="text-xs uppercase tracking-wide text-slate-400">
              C1 FAILURE rate
            </div>
            <div
              className={`mt-1 text-3xl font-mono font-semibold ${
                parseFloat(c1FailureRate) > 5
                  ? "text-red-400"
                  : "text-slate-100"
              }`}
            >
              {c1FailureRate}%
            </div>
            <div className="mt-2 text-[11px] text-slate-500 leading-snug">
              Watch: if this climbs, C1 is admitting borderline calls
              that cannot complete.
            </div>
          </div>
          <div
            className="rounded-xl p-4 border bg-slate-900/60 border-slate-800"
            data-testid="kpi-c1-outcome-coverage"
          >
            <div className="text-xs uppercase tracking-wide text-slate-400">
              Outcome coverage
            </div>
            <div className="mt-1 text-3xl font-mono font-semibold text-slate-100">
              {c1Admits > 0
                ? `${((c1AccountedFor / c1Admits) * 100).toFixed(1)}%`
                : "—"}
            </div>
            <div className="mt-2 text-[11px] text-slate-500 leading-snug">
              accounted_for / grace_admits. In-flight calls are the
              remainder.
            </div>
          </div>
        </div>

        {/* Raw JSON */}
        <details className="mt-6" open>
          <summary
            className="cursor-pointer text-xs text-slate-500 hover:text-slate-300"
            data-testid="raw-json-toggle"
          >
            View / Copy / Download JSON snapshot
          </summary>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              onClick={() => {
                if (!snapshot) return;
                const report = {
                  captured_at: new Date().toISOString(),
                  page_load_baseline_at: baseline?.generated_at ?? null,
                  live: snapshot,
                  baseline,
                  deltas: counters && baseCounters
                    ? Object.fromEntries(
                        Object.keys(counters).map((k) => [
                          k,
                          (counters[k] ?? 0) - (baseCounters[k] ?? 0),
                        ])
                      )
                    : null,
                  derived_kpis: {
                    c1_admits: c1Admits,
                    c1_hits: c1Hits,
                    c1_nomatch: c1Nomatch,
                    c1_failures: c1Failures,
                    c1_hit_rate_pct: c1HitRate,
                    c1_failure_rate_pct: c1FailureRate,
                  },
                };
                navigator.clipboard
                  .writeText(JSON.stringify(report, null, 2))
                  .then(() => {
                    const btn = document.querySelector(
                      '[data-testid="copy-json-btn"]'
                    );
                    if (btn) {
                      const orig = btn.textContent;
                      btn.textContent = "✓ Copied";
                      setTimeout(() => (btn.textContent = orig), 1500);
                    }
                  });
              }}
              className="px-3 py-1.5 text-xs rounded-md bg-emerald-800 hover:bg-emerald-700 border border-emerald-700 text-emerald-100"
              data-testid="copy-json-btn"
            >
              Copy full report to clipboard
            </button>
            <button
              onClick={() => {
                if (!snapshot) return;
                const report = {
                  captured_at: new Date().toISOString(),
                  page_load_baseline_at: baseline?.generated_at ?? null,
                  live: snapshot,
                  baseline,
                  deltas: counters && baseCounters
                    ? Object.fromEntries(
                        Object.keys(counters).map((k) => [
                          k,
                          (counters[k] ?? 0) - (baseCounters[k] ?? 0),
                        ])
                      )
                    : null,
                  derived_kpis: {
                    c1_admits: c1Admits,
                    c1_hits: c1Hits,
                    c1_nomatch: c1Nomatch,
                    c1_failures: c1Failures,
                    c1_hit_rate_pct: c1HitRate,
                    c1_failure_rate_pct: c1FailureRate,
                  },
                };
                const blob = new Blob(
                  [JSON.stringify(report, null, 2)],
                  { type: "application/json" }
                );
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                const stamp = new Date()
                  .toISOString()
                  .replace(/[:.]/g, "-");
                a.href = url;
                a.download = `c1-diagnostic-${stamp}.json`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
              }}
              className="px-3 py-1.5 text-xs rounded-md bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200"
              data-testid="download-json-btn"
            >
              Download JSON file
            </button>
            <button
              onClick={() => {
                if (!snapshot) return;
                navigator.clipboard.writeText(
                  JSON.stringify(snapshot, null, 2)
                );
              }}
              className="px-3 py-1.5 text-xs rounded-md bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300"
              data-testid="copy-live-only-btn"
            >
              Copy live pacer only
            </button>
          </div>
          <pre
            className="mt-3 p-4 bg-slate-900 border border-slate-800 rounded-lg text-[11px] text-slate-400 overflow-x-auto max-h-96"
            data-testid="raw-json-block"
          >
            {snapshot ? JSON.stringify(snapshot, null, 2) : "loading…"}
          </pre>
        </details>
      </div>
    </div>
  );
};

export default C1Diagnostics;
